import {
  Box,
  Card,
  Loader,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  WixDesignSystemProvider,
  Image,
  Button,
  Notification,
  CustomModalLayout,
  Modal,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import BG from "./pdfbg.png";
import { ReactTagManager } from "react-gtm-ts";
import React, { useEffect } from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import { useTranslation } from "react-i18next";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const SLIDE_COUNT = 4;
const INTERVAL = 4000;
const BASE_URL = `https://wix.certifiedcode.io/pdf-viewer/_functions`;

function App() {
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [InteractiveDemoOpen, setInteractiveDemoOpen] = React.useState(false);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  });

  const isFree = instanceData?.instance?.isFree !== false;

  const [index, setIndex] = React.useState<number>(0);
  const [isUpgradeBannerOpen, setIsUpgradeBannerOpen] =
    React.useState<boolean>(isFree);

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
  }, []);

  useEffect(() => {
    const sliderInterval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % SLIDE_COUNT);
    }, INTERVAL);

    return () => clearInterval(sliderInterval);
  }, []);

  useEffect(() => {
    identify(instance);
    track("dashboard_opened", { has_token: Boolean(token) });
    setInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, token]);

  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const isAppUpgraded = data.instance.isFree === false;
        setInstanceData(data);
        setIsUpgraded(isAppUpgraded);
        setIsUpgradeBannerOpen(!isAppUpgraded);
      })
      .catch(() => {});
  }

  if (token) {
    track("install_redirect");
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${WIX_APP_ID}&redirectUrl=https://wix.certifiedcode.io/pdf-viewer/_functions/@certifiedcode/base-backend/auth`;
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;
  const openInteractiveDemo = () => {
    track("watch_video_clicked");
    setInteractiveDemoOpen(true);
  };

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Notification theme="premium" show={isUpgradeBannerOpen} type={"sticky"}>
        <Notification.TextLabel>
          {t("upgradeMessage", { content: t(`content.${index}`) })}
        </Notification.TextLabel>
        <Notification.ActionButton
          type="textLink"
          link={upgradeUrl}
          target="_blank"
          onClick={() => {
            track("upgrade_clicked", { source: "banner" });
          }}
        >
          {t("upgrade-button")}
        </Notification.ActionButton>
        <Notification.CloseButton
          onClick={() => setIsUpgradeBannerOpen(false)}
        />
      </Notification>
      <Modal
        isOpen={InteractiveDemoOpen}
        screen="desktop"
        shouldDisplayCloseButton={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => setInteractiveDemoOpen(false)}
      >
        <CustomModalLayout
          removeContentPadding={true}
          content={
            <div
              style={{
                position: "relative",
                paddingBottom: "calc(56.22% + 41px)",
                height: 0,
                width: "100%",
              }}
            >
              <iframe
                src="https://demo.arcade.software/F9FfCaD6iCCqlMNQsKnc?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
                title={t("demo-title")}
                frameBorder="0"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  colorScheme: "light",
                }}
              ></iframe>
            </div>
          }
        />
      </Modal>

      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="440px" verticalAlign="middle">
              <MarketingPageLayoutContent
                title={t("pdf-title")}
                content={t("pdf-content")}
                actions={
                  <Box direction="vertical" gap="SP2">
                    <Button
                      skin="premium"
                      prefixIcon={<Icons.PremiumFilled />}
                      as="a"
                      href={upgradeUrl}
                      target="_blank"
                      onClick={() => track("upgrade_clicked", { source: "cta" })}
                    >
                      {isUpgraded ? t("pdf-manage-sub") : t("pdf-upgrade-button")}
                    </Button>
                    <Button
                      priority="secondary"
                      prefixIcon={<Icons.GetStarted />}
                      onClick={openInteractiveDemo}
                    >
                      {t("setup_guide")}
                    </Button>
                    <Button
                      skin="inverted"
                      prefixIcon={<Icons.Reviews />}
                      as="a"
                      href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
                      target="_blank"
                      onClick={() => track("write_review_clicked")}
                    >
                      {t("pdf-rate-divider-button")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          }
          image={<Image transparent borderRadius={0} src={BG} />}
        />
      </Card>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box marginTop="-260px" verticalAlign="middle">
              <MarketingPageLayoutContent
                size="medium"
                title={t("setup_title")}
                content={t("setup_content")}
                actions={
                  <Box marginTop="-30px">
                    <Button
                      size="small"
                      skin="premium"
                      prefixIcon={<Icons.PlayFilled />}
                      onClick={openInteractiveDemo}
                    >
                      {t("watch_video")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          }
        />
      </Card>
    </WixDesignSystemProvider>
  );
}

export default App;
